import { ActionIcon, Slider } from "@common/components";
import { DEFAULT_ARTWORK } from "@common/utils/constants";
import { Avatar, Transition, useMantineTheme } from "@mantine/core";
import { useHotkeys, useMediaQuery } from "@mantine/hooks";
import { Cross1Icon } from "@modulz/radix-icons";
import {
  PauseRounded,
  PlayArrowRounded,
  Shuffle,
  VolumeDown,
} from "@mui/icons-material";
import { registerListen } from "@requests/project/mixdown";
import moment from "moment";
import React, {
  createContext,
  memo,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { TbRewindBackward10, TbRewindForward10 } from "react-icons/tb";
import { useRecoilState } from "recoil";
import { ChevronUp, Minus, Repeat } from "tabler-icons-react";

import {
  audioState,
  currTimeState,
  durationState,
  loadingState,
  loopState,
  playingState,
  visibleState,
  volumeState,
} from "./player.atom";

export interface PlayerContextType {
  load: (audio: PlayerAudio) => void;
  unload: () => void;
  playPause: () => void;
  seek: (time: number) => void;
  setVisible: (visible: boolean) => void;
  toggleLoop: () => void;
}

const initialState = {} as PlayerContextType;

const PlayerContext = createContext<PlayerContextType>(initialState);
PlayerContext.displayName = "PlayerContext";

export const usePlayer = () => useContext(PlayerContext);

export type PlayerAudio = {
  src: string;
  name: string;
  artist?: string;
  artwork?: string;
  format?: string;
  file?: File;
  projectId?: string;
  mixdownId?: string;
  fileId?: string;
};

// Whether the player holds a project's mixdown. The same track can be started
// from the project page (which tags it by project) or from the mixdown file's
// own page (which only knows the file), so match on either. Matching on
// project alone made a track started on the file page look foreign to the
// project page: its play button then asked the player to load a track it
// already held, which `load` treats as a toggle, so "play" paused it.
export const holdsProjectAudio = (
  audio: PlayerAudio | null,
  projectId?: string,
  mixdownFileId?: string
) =>
  audio?.projectId === projectId ||
  (!!mixdownFileId && audio?.fileId === mixdownFileId);

function Provider({ children }) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md}px)`);
  const [loading, setLoading] = useRecoilState(loadingState);
  const [visible, setVisible] = useRecoilState(visibleState);
  const [audio, setAudio] = useRecoilState<PlayerAudio | null>(audioState);
  const [playing, setPlaying] = useRecoilState(playingState);
  const [currTime, setCurrTime] = useRecoilState(currTimeState);
  const [duration, setDuration] = useRecoilState(durationState);
  const [volume, setVolume] = useRecoilState(volumeState);
  const [loop, setLoop] = useRecoilState(loopState);

  const seeking = useRef<boolean>(false);
  const audioRef = useRef<HTMLAudioElement>();
  const [minimized, setMinimized] = useState(false);
  const listenRef = useRef<NodeJS.Timeout>();

  const handleRegisterListen = (audio: PlayerAudio | null) => {
    if (listenRef.current) clearTimeout(listenRef.current);
    listenRef.current = setTimeout(async () => {
      listenRef.current = undefined;
      if (!audio?.mixdownId) return;
      await registerListen(audio.mixdownId);
    }, 5000);
  };

  const load = (newAudioState: PlayerAudio) => {
    // If track being loaded is the same as the one that is currently
    // playing, pause/play, otherwise, stop current track and play new one
    if (audio?.src === newAudioState.src) return playPause();

    setLoading(true);
    setVisible(true);
    setCurrTime(0);

    if (!audioRef.current) {
      audioRef.current = document.createElement("audio");
      document.body.appendChild(audioRef.current);

      audioRef.current.setAttribute("src", newAudioState.src);
      audioRef.current.setAttribute("title", newAudioState.name);
      audioRef.current.setAttribute("crossOrigin", "anonymous");
      audioRef.current.setAttribute("preload", "metadata");
      audioRef.current.load();

      audioRef.current.addEventListener("loadedmetadata", (data) => {
        setDuration(audioRef.current?.duration || 0);
        setLoading(false);
        setPlaying(true);
        handleRegisterListen(newAudioState);
        audioRef.current?.play();
      });

      audioRef.current.addEventListener("error", async () => {
        setLoading(false);
        setPlaying(false);
        setAudio(null);
        setVisible(false);
      });

      audioRef.current.addEventListener("ended", () => setPlaying(false));
      audioRef.current.addEventListener("play", () => setPlaying(true));
      audioRef.current.addEventListener("pause", () => setPlaying(false));

      setAudio(newAudioState);
    } else {
      audioRef.current.setAttribute("src", newAudioState.src);
      audioRef.current.load();
      setAudio(newAudioState);
    }

    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: newAudioState.name,
        artist: newAudioState.artist || "Unknown",
        artwork: [
          {
            src: newAudioState.artwork || DEFAULT_ARTWORK,
          },
        ],
      });
    }
  };

  const unload = () => {
    listenRef.current && clearTimeout(listenRef.current);
    audioRef.current?.pause();
    audioRef.current?.remove();
    setAudio(null);
    setCurrTime(0);
    setDuration(0);
    setVisible(false);
  };

  const playPause = async () => {
    if (!audio) return;
    if (playing) {
      listenRef.current && clearTimeout(listenRef.current);
      audioRef.current?.pause();
      // Record where playback actually stopped. The sampling interval dies
      // with `playing`, so without this the last known position is up to
      // 200ms stale and the playhead settles slightly behind the true pause
      // point — visible as a small backward nudge on short tracks.
      setCurrTime(audioRef.current?.currentTime || 0);
    } else {
      handleRegisterListen(audio);
      await audioRef.current?.play();
    }
  };

  // Play or pause the audio with spacebar
  useHotkeys([
    [
      "space",
      (event) => {
        event.preventDefault();
        playPause();
      },
    ],
  ]);

  const seek = (time: number) => {
    handleSeekCommitted(null, time);
  };

  const toggleLoop = () => setLoop((prev) => !prev);

  const handleSeek = (_, value: number) => {
    if (!seeking.current) seeking.current = true;
    setCurrTime(value);
  };

  const handleSeekCommitted = (_, value: number) => {
    if (seeking.current) seeking.current = false;
    if (audioRef.current) {
      setCurrTime(value);
      audioRef.current.currentTime = value;
    }
  };

  const handleClose = () => {
    audioRef.current?.pause();
    audioRef.current?.remove();
    setAudio(null);
    setVisible(false);
  };

  const handleVolume = (_, value: number) => {
    setVolume(value);
    if (audioRef.current) audioRef.current.volume = value;
  };

  // Update Time
  useEffect(() => {
    if (!playing || !audioRef.current) return;

    const interval = setInterval(() => {
      if (seeking.current) return;
      setCurrTime(audioRef.current?.currentTime || 0);
    }, 200);

    return () => clearInterval(interval);
    // `currTime` is deliberately NOT a dependency. It used to be, but this
    // interval is what sets it — so every tick tore the interval down and
    // built a new one, making the real gap 200ms *plus* however long the
    // render took, and varying tick to tick. That irregularity was visible
    // as playhead jitter. The callback reads `audioRef.current.currentTime`
    // directly and never closes over `currTime`, so dropping it changes no
    // behaviour; re-arming on `audio` still picks up a track change.
  }, [playing, audio]);

  // Sync loop state to the native audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = loop;
    }
  }, [loop, audio]);

  const handleTenForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime += 10;
    }
  };

  const handleTenBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime -= 10;
    }
  };

  return (
    <PlayerContext.Provider
      value={{
        load,
        unload,
        playPause,
        seek,
        setVisible,
        toggleLoop,
      }}
    >
      {audio && minimized && (
        <ActionIcon
          className="animate-up fixed bottom-8 left-1/2 -translate-x-1/2 z-[1000]"
          color="purple"
          variant="light"
          size="lg"
          radius="xl"
          onClick={() => {
            setMinimized(false);
            setVisible(true);
          }}
        >
          <ChevronUp className="w-5 h-5" />
        </ActionIcon>
      )}

      <Transition
        mounted={visible && !!audio}
        transition="slide-up"
        duration={300}
      >
        {(styles) => (
          <div
            data-testid="player"
            className="fixed bottom-0 md:bottom-10 left-1/2 -translate-x-1/2 z-[1000] p-0 md:p-6"
          >
            <div
              className="w-screen md:w-[calc(100vw-4rem)] max-w-6xl p-4 md:p-6 flex gap-6 items-stretch
              bg-black bg-opacity-50 backdrop-blur-lg border border-solid border-dark-600  md:rounded-xl"
              style={styles}
            >
              <div className="flex items-center gap-4 mr-6">
                <Avatar
                  radius="md"
                  size={isMobile ? "md" : "lg"}
                  src={audio?.artwork || DEFAULT_ARTWORK}
                />

                <div>
                  <p className="m-0 font-medium max-w-[8rem] line-clamp-2 whitespace-pre-wrap">
                    {audio?.name}
                  </p>
                  <span className="text-xs font-medium md:text-sm text-primary-400">
                    {audio?.artist || "Unknown"}
                  </span>
                </div>
              </div>

              <div className="flex-1 flex flex-col items-center gap-4">
                <div className="flex items-center gap-2">
                  {!isMobile && (
                    <>
                      <ActionIcon variant="light">
                        <Shuffle />
                      </ActionIcon>

                      <ActionIcon variant="light" onClick={handleTenBackward}>
                        <TbRewindBackward10 />
                      </ActionIcon>
                    </>
                  )}

                  <ActionIcon
                    className="absolute right-8 top-1/2 -translate-y-1/2 md:top-0 md:right-0 md:relative md:translate-y-0"
                    color={!playing ? "purple" : undefined}
                    variant={!playing ? "filled" : "light"}
                    onClick={playPause}
                    loading={loading}
                  >
                    {playing ? <PauseRounded /> : <PlayArrowRounded />}
                  </ActionIcon>

                  {!isMobile && (
                    <>
                      <ActionIcon variant="light" onClick={handleTenForward}>
                        <TbRewindForward10 />
                      </ActionIcon>

                      <ActionIcon
                        data-testid="player-loop"
                        variant={loop ? "filled" : "light"}
                        color={loop ? "purple" : undefined}
                        onClick={toggleLoop}
                      >
                        <Repeat />
                      </ActionIcon>
                    </>
                  )}
                </div>

                <div className="flex flex-1 items-center w-full gap-4">
                  <p className="hidden md:block m-0">
                    {moment.utc(currTime * 1000).format("mm:ss")}
                  </p>

                  <Slider
                    className="absolute bottom-0 left-0 p-0 w-full md:relative"
                    color={theme.colors.purple[6]}
                    value={currTime}
                    min={0}
                    max={duration}
                    step={1}
                    size="small"
                    valueLabelDisplay="off"
                    onChange={handleSeek}
                    onChangeCommitted={handleSeekCommitted}
                  />

                  <p className="hidden md:block m-0">
                    {moment.utc(duration * 1000).format("mm:ss")}
                  </p>
                </div>
              </div>

              <div className="hidden md:flex items-end gap-2 w-24 mt-8">
                <VolumeDown className="-translate-y-1" />

                <Slider
                  color={theme.colors.purple[6]}
                  value={volume}
                  min={0}
                  max={1}
                  step={0.05}
                  size="small"
                  valueLabelDisplay="off"
                  onChange={handleVolume}
                />
              </div>

              <div className="flex items-center gap-2 invisible md:visible absolute top-4 right-4">
                <ActionIcon
                  onClick={() => {
                    setMinimized(true);
                    setVisible(false);
                  }}
                  variant="transparent"
                  size="sm"
                >
                  <Minus />
                </ActionIcon>

                <ActionIcon
                  onClick={handleClose}
                  variant="transparent"
                  size="sm"
                >
                  <Cross1Icon />
                </ActionIcon>
              </div>
            </div>
          </div>
        )}
      </Transition>

      {children}
    </PlayerContext.Provider>
  );
}

export const PlayerProvider = memo(Provider);
